import { onLocaleChange, t } from '../i18n';

export type UiEnvironment = 'Dev' | 'Test' | 'Acc' | 'Prod';

export type UiConfigResponse = {
  environment: string;
};

const MARKED_ENVS = new Set<UiEnvironment>(['Dev', 'Test', 'Acc']);

const ENV_COLORS: Record<Exclude<UiEnvironment, 'Prod'>, string> = {
  Dev: '#2563eb',
  Test: '#0d9488',
  Acc: '#d97706',
};

let currentEnvironment: UiEnvironment = 'Prod';
let documentChromeApplied = false;
let localeUnsub: (() => void) | null = null;

export function normalizeUiEnvironment(value: string | null | undefined): UiEnvironment {
  if (!value) return 'Prod';
  const trimmed = value.trim();
  if (trimmed.toLowerCase() === 'dev') return 'Dev';
  if (trimmed.toLowerCase() === 'test') return 'Test';
  if (trimmed.toLowerCase() === 'acc') return 'Acc';
  if (trimmed.toLowerCase() === 'prod') return 'Prod';
  return 'Prod';
}

export function getUiEnvironment(): UiEnvironment {
  return currentEnvironment;
}

export function isMarkedUiEnvironment(env: UiEnvironment = currentEnvironment): boolean {
  return MARKED_ENVS.has(env);
}

/** Test/helper: set environment without fetching. */
export function setUiEnvironmentForTests(env: UiEnvironment): void {
  currentEnvironment = env;
}

export async function loadUiConfig(): Promise<void> {
  try {
    const response = await fetch('/api/ui-config');
    if (!response.ok) return;
    const data = (await response.json()) as UiConfigResponse;
    currentEnvironment = normalizeUiEnvironment(data.environment);
  } catch {
    // Safe default: leave Prod (no marking).
  }
}

export function applyEnvDocumentChrome(): void {
  updateDocumentTitle();
  updateFavicon();

  if (!documentChromeApplied) {
    documentChromeApplied = true;
    localeUnsub = onLocaleChange(() => {
      updateDocumentTitle();
    });
  }
}

export function createEnvBanner(): HTMLElement | null {
  if (!isMarkedUiEnvironment()) return null;

  const env = currentEnvironment as Exclude<UiEnvironment, 'Prod'>;
  const banner = document.createElement('div');
  banner.className = `env-banner env-banner--${env.toLowerCase()}`;
  banner.setAttribute('role', 'status');

  const label = document.createElement('span');
  label.className = 'env-banner__label';
  label.setAttribute('data-i18n', bannerKey(env));
  banner.append(label);
  return banner;
}

function bannerKey(env: Exclude<UiEnvironment, 'Prod'>): string {
  switch (env) {
    case 'Dev':
      return 'env.banner.dev';
    case 'Test':
      return 'env.banner.test';
    case 'Acc':
      return 'env.banner.acc';
  }
}

function titleSuffixKey(env: Exclude<UiEnvironment, 'Prod'>): string {
  switch (env) {
    case 'Dev':
      return 'env.titleSuffix.dev';
    case 'Test':
      return 'env.titleSuffix.test';
    case 'Acc':
      return 'env.titleSuffix.acc';
  }
}

function updateDocumentTitle(): void {
  const appName = t('app.name');
  if (!isMarkedUiEnvironment()) {
    document.title = appName;
    return;
  }
  const env = currentEnvironment as Exclude<UiEnvironment, 'Prod'>;
  const suffix = t(titleSuffixKey(env));
  document.title = `${appName} (${suffix})`;
}

function updateFavicon(): void {
  const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  const link = existing ?? document.createElement('link');
  link.rel = 'icon';
  link.type = 'image/svg+xml';

  if (!isMarkedUiEnvironment()) {
    link.href = '/icons/logo.svg';
  } else {
    const env = currentEnvironment as Exclude<UiEnvironment, 'Prod'>;
    link.href = buildFaviconDataUrl(ENV_COLORS[env]);
  }

  if (!existing) {
    document.head.append(link);
  }
}

function buildFaviconDataUrl(color: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="${color}" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2 4 5v6c0 5 3.4 8.6 8 11 4.6-2.4 8-6 8-11V5z"/><circle cx="9.5" cy="11" r="1.6"/><circle cx="15" cy="8.5" r="1.6"/><circle cx="15" cy="14.5" r="1.6"/><path d="m10.9 10.2 2.7-1.1"/><path d="m10.9 11.8 2.7 1.6"/></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

/** Reset module state between tests. */
export function resetEnvChromeForTests(): void {
  currentEnvironment = 'Prod';
  documentChromeApplied = false;
  localeUnsub?.();
  localeUnsub = null;
}
